import { useEffect, useState } from 'react';
import propTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { DocumentTextIcon, PencilSquareIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

import { esPdf } from '../../../api/brechaGenero';
import { FormHeader, FormSection, CheckboxField, SubmitButton } from './FormFields';

/**
 * Paso 3: revisar los PDF generados antes de mandarlos a firma.
 *
 * Se previsualizan en un iframe y, si alguno ha salido mal, se sustituye por una
 * versión corregida sin rehacer el formulario entero.
 *
 * El .docx del contrato queda aparte: es la copia editable que se descarga el
 * agente para corregirla, no se previsualiza (el navegador no sabe pintarlo) ni
 * se manda a firma.
 *
 * Sin número de negocio no hay expediente al que asociar la firma, así que en
 * ese caso solo se revisa y se cierra.
 */
const RevisionDocumentos = ({ documentos, onReemplazar, onEnviar, onListo, onBack, enviando, puedeEnviar }) => {
    const { t } = useTranslation();
    const [urls, setUrls] = useState([]);
    const [visibles, setVisibles] = useState({});
    const [editando, setEditando] = useState(false);

    // Una URL de objeto por PDF, revocadas al cambiar o desmontar: si no, los
    // blobs se quedan retenidos en memoria toda la sesión. Se mantiene el índice
    // original del array para no descuadrar las referencias.
    useEffect(() => {
        const creadas = documentos.map(({ nombre, blob }) => (esPdf(nombre) ? URL.createObjectURL(blob) : null));
        setUrls(creadas);
        return () => creadas.forEach((url) => url && URL.revokeObjectURL(url));
    }, [documentos]);

    const alternar = (indice) => setVisibles((prev) => ({ ...prev, [indice]: !prev[indice] }));

    const conIndice = documentos.map((documento, indice) => ({ ...documento, indice }));
    const paraFirma = conIndice.filter((documento) => esPdf(documento.nombre));
    const editables = conIndice.filter((documento) => !esPdf(documento.nombre));

    return (
        <div>
            <FormHeader
                emoji="🖊"
                title={t('Revisa los documentos antes de')}
                highlight={t('enviarlos a firma')}
                onBack={onBack}
                backLabel={t('Volver atrás')}
            />

            <FormSection
                title={t('Documentos que se envían a firma')}
                description={t('Previsualízalos antes de mandarlos: una vez enviados, el cliente los recibe tal cual')}
            >
                <div className="col-span-1 sm:col-span-2 space-y-4">
                    {paraFirma.map((documento) => (
                        <div key={documento.nombre + documento.indice} className="rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 p-4">
                            <div className="flex items-center justify-between gap-3">
                                <span className="flex items-center gap-2 min-w-0 text-sm text-gray-700 dark:text-gray-300">
                                    <DocumentTextIcon className="w-5 h-5 flex-shrink-0 text-gray-500 dark:text-gray-400" />
                                    <span className="truncate">{documento.nombre}</span>
                                    {documento.reemplazado && (
                                        <span className="flex-shrink-0 text-xs text-amber-600 dark:text-amber-400">
                                            {t('sustituido por')} {documento.reemplazado}
                                        </span>
                                    )}
                                </span>
                                {urls[documento.indice] && (
                                    <button
                                        type="button"
                                        onClick={() => alternar(documento.indice)}
                                        className="flex-shrink-0 text-sm text-primary-600 hover:underline dark:text-primary-400"
                                    >
                                        {visibles[documento.indice] ? t('Ocultar previsualización') : t('Previsualizar')}
                                    </button>
                                )}
                            </div>

                            {visibles[documento.indice] && urls[documento.indice] && (
                                <div className="mt-3 border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
                                    <iframe src={urls[documento.indice]} width="100%" height="700px" title={documento.nombre}></iframe>
                                </div>
                            )}

                            {editando && (
                                <div className="mt-3">
                                    <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white" htmlFor={`reemplazo_${documento.indice}`}>
                                        {t('Sustituir por otro PDF')}
                                    </label>
                                    <input
                                        id={`reemplazo_${documento.indice}`}
                                        type="file"
                                        accept=".pdf"
                                        disabled={enviando}
                                        className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 dark:text-gray-400 focus:outline-none dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400"
                                        onChange={(e) => {
                                            const fichero = e.target.files[0];
                                            if (fichero) onReemplazar(documento.indice, fichero);
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    ))}

                    <CheckboxField
                        name="editar_documentos"
                        label={t('¿Quieres corregir alguno de los PDF?')}
                        description={t('El documento sustituido conserva su nombre, así que el envío a firma no cambia')}
                        checked={editando}
                        onChange={(_, value) => setEditando(value)}
                        disabled={enviando}
                    />
                </div>
            </FormSection>

            {editables.length > 0 && (
                <FormSection title={t('Copia editable')}>
                    <div className="col-span-1 sm:col-span-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 p-4">
                        {editables.map((documento) => (
                            <div key={documento.nombre + documento.indice} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                                <PencilSquareIcon className="w-5 h-5 flex-shrink-0 text-gray-500 dark:text-gray-400" />
                                <span className="truncate">{documento.nombre}</span>
                            </div>
                        ))}
                        <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                            {t('Descargado en tu equipo. No se envía a firma: está para que puedas corregir el contrato si algo no cuadra, exportarlo a PDF y sustituirlo arriba.')}
                        </p>
                    </div>
                </FormSection>
            )}

            {!puedeEnviar && (
                <div className="mb-6 rounded-lg border border-amber-200 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-900/20 p-4">
                    <div className="flex gap-3 text-sm text-amber-900 dark:text-amber-100">
                        <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        <p>{t('Sin número de negocio no hay expediente al que asociar la firma: los documentos ya están descargados en tu equipo.')}</p>
                    </div>
                </div>
            )}

            <div className="mt-10">
                {puedeEnviar ? (
                    <form onSubmit={(e) => { e.preventDefault(); onEnviar(); }}>
                        <SubmitButton
                            loading={enviando}
                            label={t('Enviar a firma')}
                            loadingLabel={t('Enviando...')}
                        />
                    </form>
                ) : (
                    <button
                        type="button"
                        onClick={onListo}
                        className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-4 focus:ring-primary-300"
                    >
                        {t('¡Listo!')}
                    </button>
                )}
            </div>
        </div>
    );
};

RevisionDocumentos.propTypes = {
    documentos: propTypes.array.isRequired,
    onReemplazar: propTypes.func.isRequired,
    onEnviar: propTypes.func.isRequired,
    onListo: propTypes.func.isRequired,
    onBack: propTypes.func.isRequired,
    enviando: propTypes.bool,
    puedeEnviar: propTypes.bool,
};

export default RevisionDocumentos;
